import axios from 'axios'
import { extractError } from '../utils/errorHandler'
import { ndjsonAdapter } from '../utils/ndjsonAdapter'

export const getById = async (id, include = false) => {
  try {
    let includeString = include !== false ? '?include=process,material' : ''
    const { data: workOrder } = await axios.get(`workOrder/${id}${includeString}`)
    return workOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const getWorkOrderErrors = async (workStationId, id, producedGoodQuantity, producedBadQuantity) => {
  try {
    const { data: workOrder } = await axios.get(
      `workOrder/can-end-execution/${workStationId ?? -2}/${id}/${producedGoodQuantity ?? 0}/${producedBadQuantity ?? 0}`
    )
    return workOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const getWorkOrderStartErrors = async (workStationId, id) => {
  try {
    const { data: workOrder } = await axios.get(`workOrder/can-start-execution/${workStationId}/${id}`)
    return workOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

/**
 * Validações em streaming: onProgress recebe cada evento ({ type: 'plan' | 'hookStarted' | 'hookFinished' |
 * 'heartbeat', steps, step }) enquanto os webhooks são consultados; o retorno é o mesmo das rotas acima.
 * signal (AbortController) cancela a consulta, inclusive o webhook em andamento no servidor.
 */
const streamValidation = async (path, { onProgress, signal } = {}) => {
  try {
    const { data } = await axios.get(path, {
      adapter: ndjsonAdapter,
      onStreamMessage: onProgress,
      streamSignal: signal,
    })
    return data
  } catch (err) {
    if (err && err.name === 'AbortError') throw err
    if (err && !err.response) throw new Error(err.message || 'sem resposta do servidor')
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const streamWorkOrderStartErrors = (workStationId, id, options) =>
  streamValidation(`workOrder/can-start-execution/${workStationId}/${id}/stream`, options)

export const streamWorkOrderErrors = (workStationId, id, producedGoodQuantity, producedBadQuantity, options) =>
  streamValidation(
    `workOrder/can-end-execution/${workStationId ?? -2}/${id}/${producedGoodQuantity ?? 0}/${producedBadQuantity ?? 0}/stream`,
    options
  )

export const streamWorkOrderTrackingErrors = (workStationId, id, producedGoodQuantity, producedBadQuantity, options) =>
  streamValidation(
    `workOrder/can-track-execution/${workStationId}/${id}/${producedGoodQuantity ?? 0}/${producedBadQuantity ?? 0}/stream`,
    options
  )

export const getWorkOrderTrackingErrors = async (workStationId, id, producedGoodQuantity, producedBadQuantity) => {
  try {
    const { data: workOrder } = await axios.get(
      `workOrder/can-track-execution/${workStationId}/${id}/${producedGoodQuantity ?? 0}/${producedBadQuantity ?? 0}`
    )
    return workOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const getMaterialDetails = async (id) => {
  try {
    const { data: workOrderMaterialDetails } = await axios.get(`workOrder/${id}/materialsDetails`)
    return workOrderMaterialDetails
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const getLastWorkOrder = async (data) => {
  try {
    const { data: productionTracking } = await axios.get('workOrder/last', data)
    return productionTracking
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const create = async (data) => {
  try {
    const { data: productionTracking } = await axios.post(
      'workOrder',
      data.map((d) => ({
        ...d,
        time: d.time ? d.time : null,
        workStation:
          d.workStation && d.workStation.filter((c) => c).length > 0 ? d.workStation : [],
      }))
    )
    return productionTracking
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const remove = async (id) => {
  try {
    const { data: productionTracking } = await axios.delete('workOrder/' + id)
    return productionTracking
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const removeAll = async (id) => {
  try {
    const { data: productionTracking } = await axios.delete('workOrder/' + id + '/all')
    return productionTracking
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const getRealLaborTime = async (id) => {
  try {
    const { data: productionTracking } = await axios.get(`workOrder/${id}/realLaborTime`)
    return productionTracking
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const getProducedQuantitiesHistory = async (id) => {
  try {
    const { data } = await axios.get(`workOrder/${id}/producedQuantitiesHistory`)
    return data
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const getAllByCode = async (code) => {
  try {
    const { data: workOrder } = await axios.get(`workOrder/allByCode?code=${code}`)
    return workOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const getByCode = async (code, operation) => {
  try {
    const { data: workOrder } = await axios.get(`workOrder?code=${code}&operation=${operation}`)
    return workOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const reset = async (workOrder) => {
  try {
    const { data: updatedWorkOrder } = await axios.post(
      `workOrder/${workOrder.id}/reset`,
      workOrder
    )
    return updatedWorkOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const updateWorkOrderNote = async (workOrder) => {
  try {
    if (workOrder.workNote?.workOrderNoteDto)
      return await updateWorkOrderNoteOrProperties(workOrder.workNote)

    const { data: updatedWorkOrder } = await axios.patch('workOrder/updateWorkOrderNote', workOrder)
    return updatedWorkOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const updateWorkOrderNoteOrProperties = async (workOrder) => {
  try {
    const { data: updatedWorkOrder } = await axios.patch(
      'workOrder/updateWorkOrderNoteOrProperties',
      workOrder
    )
    return updatedWorkOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const updateProducedQuantities = async (producedQuantities) => {
  try {
    await axios.patch('workOrder/updateProducedQuantities', producedQuantities)
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

export const cancel = async (workOrderId) => {
  try {
    const { data: cancelledWorkOrder } = await axios.post(`workOrder/${workOrderId}/cancel`)
    return cancelledWorkOrder
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}

/**
 * Ordens programadas e não executadas das estações com data de entrega ou atrasadas em relação à
 * programação (indicador de risco de atraso do Planejamento). Vem a fila inteira, não só o período
 * do calendário.
 */
export const getDeliveryRisk = async (workStationIds) => {
  try {
    const params = new URLSearchParams()
    ;(workStationIds || []).forEach((id) => params.append('workStationIds', id))
    const { data } = await axios.get('workOrder/delivery-risk', { params })
    return data
  } catch (err) {
    const apiError = extractError(err)
    throw new Error(apiError.message || apiError.Message)
  }
}
